import { useCallback, useMemo, useState } from "react";
import { Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { DefaultButton } from "../common/components/DefaultButton";
import { Col, Row } from "../common/components/Grid";
import useClientConfig from "../common/hooks/clientConfigHook";
import { ReactNodeDialog } from "../common/utils/dialogUtlities";
import { buildShadedColor, makeTableHeader } from "../common/utils/tableUtilities";
import { getDescriptor, useJob } from "../reducers/jobSlice";
import { usePriorities } from "../reducers/prioritySlice";
import JobSearch from "./JobSearchPanel";
import PropTypes from 'prop-types';
import { FloatingLabel } from "../common/components/FloatingLabel";

export const customCtrlDef = {
    control: ({ value, placeholder, onChange, ...props }) => <FloatingLabel value={value} placeholder={placeholder}>
        <JobTable jobs={value ?? []} canSelect={() => false} onAdd={v => onChange(value ? [ ...value, v.jobid ] : [v.jobid])} onRemove={v => onChange(value ? value.filter(v2 => v.jobid !== v2) : [])} {...props}></JobTable>
    </FloatingLabel>
}

const JobTableRow = ({jobid, fields, canSelect, onSelect, onRemove, disabled}) => {
    const clientConfig = useClientConfig();
    const job = useJob(jobid);
    const priorities = usePriorities();

    const priority = useMemo(() => priorities.find(p => p.internalid === job?.priority), [priorities, job?.priority]);
    const color = useMemo(() => buildShadedColor(priority?.color), [priority?.color]);

    const handleSelect = (job) => {
        if (canSelect?.(job)) {
            onSelect?.(job);
        }
    }

    if (!job) { 
        return null;
    }

    return <><tr style={{ ...color, cursor: canSelect(job) ? 'pointer' : 'not-allowed' }} onClick={() => handleSelect(job)}>
        {Object.keys(fields).map(f => <td key={f}>{job[f]}</td>)}
            <td className="text-right">
            { disabled || !onRemove ? null : <DefaultButton kind="delete" variant="outline-dark" size="sm" onClick={e => { e.preventDefault(); e.stopPropagation(); onRemove(job); }} hideLabel={true}></DefaultButton> }
        </td>
    </tr>
    {clientConfig.jobListDescriptor ? <tr style={{ ...color, cursor: canSelect(job) ? 'pointer' : 'not-allowed' }} onClick={() => handleSelect(job)}>
        <td colSpan={Object.keys(fields).length + 1}>{getDescriptor(clientConfig.jobListDescriptor, job)}</td>
    </tr> : null }
    </>
}
JobTableRow.propTypes = {
    jobid: PropTypes.string,
    fields: PropTypes.object,
    canSelect: PropTypes.func,
    onSelect: PropTypes.func,
    onRemove: PropTypes.func,
    disabled: PropTypes.bool
}

const JobTable = ({ jobs, exclude, searchFilter, canSelect, onSelect, onAdd, onRemove, disabled }) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const [sort, setSort] = useState(['displayid']);
    const [showSearch, setShowSearch] = useState(false);

    const fields = useMemo(() => {
        const info = {};
        info.displayid = t('jobNumber');
        info.status = t('status_one');
        if (clientConfig.site) {
            info.site = t('site', { count: 1 });
        }
        if (clientConfig.level) {
            info.level = t('level', { count : 1 });
        }
        info.type = t('type', { count: 1 });
        info.problem = t('problem', { count: 1 });
        info.location = t('location', { count: 1 });
        return info;
    }, [t, clientConfig]);

    const handleSelect = useCallback(job => {
        if (!jobs || jobs.every(j => job.jobid.toString() !== j.toString())) {
            onAdd(job);
        }
        setShowSearch(false);
    }, [jobs, onAdd]);

    const searchExcludes = useMemo(() => jobs.concat(exclude ?? []), [jobs, exclude]);

    const header = makeTableHeader(Object.entries(fields).map(([field, title]) => ({ field, title })).concat([{title: '', key: 'del'}]), sort, setSort)
    const tableBody = jobs.map(jobid => <JobTableRow key={jobid} jobid={jobid} canSelect={canSelect} onSelect={onSelect} onRemove={onRemove} disabled={disabled} fields={fields}></JobTableRow>);

    return <>
    <ReactNodeDialog title={t('addJobRelation')} showModal={showSearch} size="xlg" onClose={() => setShowSearch(false)}><div className="w-100" style={{height: 'calc(100dvh - 190px)'}}><JobSearch filter={searchFilter} exclude={searchExcludes} onSelect={handleSelect}></JobSearch></div></ReactNodeDialog>
    <Row>
        <Col>
            <Table size="sm">
                <thead>
                    <tr>
                        {header}
                    </tr>
                </thead>
                <tbody>
                    {tableBody}
                </tbody>
            </Table>
        </Col>
    </Row>
    { disabled || !onAdd ? null : <Row>
        <Col className="text-right">
            <DefaultButton kind="add" onClick={() => setShowSearch(true)}></DefaultButton>
        </Col>
    </Row> }
    </>
}
JobTable.propTypes = {
    jobs: PropTypes.arrayOf(PropTypes.string),
    exclude: PropTypes.arrayOf(PropTypes.string),
    filter: PropTypes.func,
    canSelect: PropTypes.func,
    onSelect: PropTypes.func,
    onAdd: PropTypes.func,
    onRemove: PropTypes.func,
    disabled: PropTypes.bool
}

export default JobTable;